import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild
} from '@angular/core';
import { Subscription } from 'rxjs';
import { DateTime } from 'luxon';
import { BannerNotificationsService, LocalStorageService } from '../../../xform-compat';
import { PerformanceDashboardService } from '../../../core/data-services';
import { ChartService, RmsUtilsService } from '../../../core/services';
import { PerformanceDashboardChart } from '../../models';
import { FinancialUtilsService } from '../../../core/services';
import { EventsHubService } from '../../../core/events-hub.service';
import { init as echartsInit } from 'echarts';
import { RmsColors } from '../../constants/rms-colors';
import {DateRange} from "../date-picker/date-picker.component";
import { isMobileViewport } from '../../constants/viewport';


/**
 * Component class for performance dashboard chart
 */
@Component({
    selector: 'rms-performance-chart',
    templateUrl: './performance-chart.component.html',
    styleUrls: ['./performance-chart.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class PerformanceChartComponent implements OnInit, OnChanges, OnDestroy {

  @Input() portfolioId: number;

  @ViewChild('performanceChart', {static: true}) performanceChartElement: ElementRef;
  private performanceChart: any;

  private readonly selectedStateStorageKey = 'performanceChartSelectedState';
  private selectedState = {};
  private enableLocalStorage = true;

  private chartData: Array<PerformanceDashboardChart>;
  private lnDataMap: Map<string, Map<string, number>>;
  private raNetOfFeesData: Array<[string, number, number]> = [];

  public chartDateRange: DateRange;
  private minDate: Date;
  private maxDate: Date;
  private raCapitalPortfolioMinDate: string;
  private daysBetween: number;
  private chartStartDate: string;
  private chartEndDate: string;

  private moodIndexPresetDateMap: Map<string, string> = new Map<string, string>();

  public chartDatePortion: string;

  public loadingChartData = false;

  /** Single placeholder over the empty range field, as on Headlines; the picker's own two read as one off-centre hint. */
  public readonly datePlaceholder = 'M/D/Y';

  private timerSubscription: Subscription;

  private moodIndexColorIndex = 0;
  private readonly moodIndexColors = ['#7F99A8', '#306A9A', '#66A6BF', '#9F3E52', '#9EBE61', '#21908F', '#2C694F', '#5D4580', '#D38359', '#1E7BB5'];

  private readonly CHART_DATA_RANGE = -1;

  private readonly MOOD_INDEX_DAY_BUFFER = 4;

  private readonly RA_CAPITAL_PORTFOLIO = 'RA Capital';
  private readonly GENERIC_PORTFOLIO = 'GENERIC_PORTFOLIO';
  private readonly SNP_500 = 'S&P 500';
  private readonly RUSSEL_2000 = 'Russell 2000';
  private readonly IBB_INDEX = 'IBB';
  private readonly XBI_INDEX = 'XBI';
  private readonly RA_NET_OF_FEES = 'RA Net of Fees';
  private readonly RA_LONG_PUBLIC = 'RA Long Public';
  private readonly YE23_MOOD_INDEX = 'YE23 Core Biotech (RA)';
  private readonly YE23_MA_MOOD_INDEX = 'YE23 Core Biotech -M&A (RA)';
  private readonly YE20_MOOD_INDEX = 'YE20 Core Biotech (RA)';
  private readonly YE20_MA_MOOD_INDEX = 'YE20 Core Biotech -M&A (RA)';
  private readonly YE24_MOOD_INDEX = 'YE24 Core Biotech (RA)';
  private readonly YE24_MA_MOOD_INDEX = 'YE24 Core Biotech -M&A (RA)';

  /**
   * Short series labels for the hover card and the phone readout; mood indexes are shortened by pattern.
   * A Map, not an object literal, so a series named like an Object.prototype key ('constructor') can't
   * look up an inherited function.
   */
  private readonly TOOLTIP_LABELS: ReadonlyMap<string, string> = new Map([
    [this.RA_CAPITAL_PORTFOLIO, 'RA'],
    [this.RA_LONG_PUBLIC, 'RA Long'],
    [this.RA_NET_OF_FEES, 'RA Net'],
    [this.IBB_INDEX, 'IBB'],
    [this.XBI_INDEX, 'XBI'],
    [this.SNP_500, 'S&P'],
    [this.RUSSEL_2000, 'R2K']
  ]);
  private readonly TOOLTIP_DATE_FORMAT = 'M/d/yy';
  /** The year-end Core Biotech indexes the MOOD button stands for, e.g. `YE24 Core Biotech -M&A (RA)`. */
  private readonly CORE_BIOTECH_MOOD_INDEX = /^(YE\d+) Core Biotech( -M&A)? \(RA\)$/;

  /** Whether the current chart options are the phone layout; compared on resize to re-lay out across the breakpoint. */
  private chartIsMobile = false;
  /** Set while generateSeries runs, so the legend events autoDisableIndex fires don't re-lay out a half-built chart. */
  private generatingSeries = false;
  /** Rendered legend height on phones, which sets the plot's top and where the readout sits. */
  private mobileLegendHeight: number;
  /** True while a finger is on the phone chart, when the legend is hidden behind the readout. */
  private legendHiddenForTouch = false;
  /** Phone chart height with a two-row legend; the chart grows past it by however much taller the legend is. */
  private readonly MOBILE_CHART_HEIGHT = 328;
  private readonly MOBILE_LEGEND_HEIGHT = 32;
  private readonly MOBILE_LEGEND_PLOT_GAP = 20;

  constructor(
    private performanceDashboardService: PerformanceDashboardService,
    private bannerNotificationService: BannerNotificationsService,
    private cd: ChangeDetectorRef,
    private eventsHubService: EventsHubService,
    private chartService: ChartService,
    private localStorageService: LocalStorageService,
    private elementRef: ElementRef
  ) {
    this.chartDatePortion = 'YTD';
    this.chartStartDate = `${DateTime.now().year}-01-01`;
  }


  ngOnInit() {
    this.performanceChart = echartsInit(this.performanceChartElement.nativeElement);
    this.initOnWindowResize();
    const chartElement: HTMLElement = this.performanceChartElement.nativeElement;
    chartElement.addEventListener('touchstart', this.onChartTouchStart, {passive: true});
    chartElement.addEventListener('touchend', this.onChartTouchEnd, {passive: true});
    chartElement.addEventListener('touchcancel', this.onChartTouchEnd, {passive: true});
    this.initOnDataZoom();
    this.initOnLegendSelect();
    this.getChartData();
    this.timerSubscription = this.eventsHubService.dashboardTimerCompletes$.subscribe(() =>  {
      this.getChartData(false);
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.portfolioId && !changes.portfolioId.firstChange) {
      this.getChartData();
    }
  }

  ngOnDestroy() {
    this.timerSubscription.unsubscribe();
    // The listener and the echarts instance both hold this component alive. Without these two the
    // dashboard accumulates one of each per visit, and every stale listener still resizes its chart.
    window.removeEventListener('resize', this.onWindowResize, true);
    const chartElement: HTMLElement = this.performanceChartElement.nativeElement;
    chartElement.removeEventListener('touchstart', this.onChartTouchStart);
    chartElement.removeEventListener('touchend', this.onChartTouchEnd);
    chartElement.removeEventListener('touchcancel', this.onChartTouchEnd);
    this.performanceChart?.dispose();
  }

  public onDateRangeChanged(event: {startDate: DateTime; endDate: DateTime}) {
    if (!event || (event.startDate === null && event.endDate === null)) {
      this.filterChart('YTD');
      this.chartDatePortion = 'YTD';
      return;
    }
    const filterBtn = document.querySelector('.btn-group-toggle .active');
    if (filterBtn) {
      filterBtn.classList.remove('active');
    }
    this.chartDatePortion = null;
    const startValue = event.startDate ? event.startDate.toISO() : null;
    const endValue = event.endDate ? event.endDate.toISO() : null;
    this.performanceChart.dispatchAction({
      type: 'dataZoom',
      startValue: startValue,
      endValue: endValue
    });
  }

  public filterChart(data: string) {
    const rangeField = document.querySelector('.btnclear') as HTMLElement;
    if (rangeField) {
      rangeField.click();
    }
    let startDate: string;
    switch (data) {
      case '10 D':
        startDate = DateTime.now().minus({ days: 10 }).toFormat('yyyy-MM-dd');
        break;
      case '1M':
        startDate = DateTime.now().minus({ months: 1 }).toFormat('yyyy-MM-dd');
        break;
      case 'YTD':
        startDate = `${DateTime.now().year}-01-01`;
        break;
      case '1Y':
        startDate = DateTime.now().minus({ years: 1 }).toFormat('yyyy-MM-dd');
        break;
      case 'MAX':
        startDate = this.raCapitalPortfolioMinDate;
        break;
      default:
        if (this.moodIndexPresetDateMap.has(data)) {
          startDate = this.moodIndexPresetDateMap.get(data);
        }
        break;
    }
    if (this.performanceChart) {
      this.performanceChart.dispatchAction({
        type: 'dataZoom',
        startValue: startDate ? startDate : null,
        start: startDate ? null : 0,
        end: 100
      });
    }
  }

  /**
   * Held as a field rather than an inline closure so ngOnDestroy can pass removeEventListener the
   * same reference addEventListener was given — an inline arrow can never be removed.
   */
  private readonly onWindowResize = () => {
    this.performanceChart?.resize();
    // The phone and desktop layouts are different option sets, so crossing the breakpoint rebuilds the chart.
    // After the resize, so the phone legend wraps (and is measured) at the new width rather than the old one.
    if (this.chartData && isMobileViewport() !== this.chartIsMobile) {
      this.initChart();
    } else {
      // A narrower phone can wrap the legend onto another row.
      this.applyMobileLegend();
    }
  };

  /**
   * The phone readout sits just above the plot and is shorter than the legend, so the legend is hidden
   * while a finger is down rather than showing above and behind it. grid.top is set explicitly, so
   * hiding the legend does not move the plot. The legend is only hidden when the touch starts on the plot,
   * so taps on the legend still reach the legend.
   */
  private readonly onChartTouchStart = (event?: TouchEvent) => {
    if (!this.chartIsMobile || this.legendHiddenForTouch) {
      return;
    }
    const touch = event?.touches?.[0];
    if (touch) {
      const rect = (this.performanceChartElement.nativeElement as HTMLElement).getBoundingClientRect();
      const point = [touch.clientX - rect.left, touch.clientY - rect.top];
      if (!this.performanceChart?.containPixel('grid', point)) {
        return;
      }
    }
    this.legendHiddenForTouch = true;
    this.performanceChart?.setOption({legend: {show: false}});
  };

  /**
   * Lifting the finger hides the phone readout and brings the legend back. Phones only: a wider touch
   * device shows the desktop hover card, which should stay up after a tap so its values can be read.
   */
  private readonly onChartTouchEnd = () => {
    if (!this.chartIsMobile) {
      return;
    }
    this.performanceChart?.dispatchAction({type: 'hideTip'});
    if (this.legendHiddenForTouch) {
      this.legendHiddenForTouch = false;
      this.performanceChart?.setOption({legend: {show: true}});
      // Catches any legend change made while it was hidden, when it could not be measured.
      this.applyMobileLegend();
    }
  };

  private initOnWindowResize() {
    window.addEventListener('resize', this.onWindowResize, true);
  }

  public toggleIbbXbi() {
    this.toggleIndex(this.getIbbXbiCurrentState(), [this.IBB_INDEX, this.XBI_INDEX]);
  }

  public getIbbXbiCurrentState() {
    return this.selectedState[this.IBB_INDEX] || this.selectedState[this.XBI_INDEX];
  }

  public toggleMood() {
    const names = new Array<string>();
    for (const key of Object.keys(this.selectedState)) {
      if (this.isMoodIndexSeries(key)) {
        names.push(key);
      }
    }
    this.toggleIndex(this.getMoodCurrentState(), names);
  }

  public getMoodCurrentState() {
    let currentState = false;
    for (const [key, value] of Object.entries(this.selectedState)) {
      if (this.isMoodIndexSeries(key)) {
        currentState = currentState || Boolean(value);
      }
    }
    return currentState;
  }

  public getSortedMoodIndexPresets() {
    return Array.from(this.moodIndexPresetDateMap.keys()).sort();
  }

  /**
   * Whether the empty-field overlay shows. Ported from Headlines: the model alone can't tell, because
   * Material writes null for half-typed text, so the inputs are read as well.
   */
  public get showDatePlaceholder(): boolean {
    return !this.chartDateRange?.startDate && !this.chartDateRange?.endDate && !this.dateInputsHaveText();
  }

  /** Whether either half of the range control holds text, read from the DOM on every call (see Headlines). */
  private dateInputsHaveText(): boolean {
    const inputs = this.elementRef.nativeElement.querySelectorAll('.mat-date-range-input-inner');
    return Array.from(inputs).some((input: HTMLInputElement) => input.value?.trim().length > 0);
  }

  private toggleIndex(currentState: boolean, names: Array<string>) {
    names.forEach(name => {
      this.performanceChart.dispatchAction({
        type: currentState ? 'legendUnSelect' : 'legendSelect',
        name: name
      });
    });
  }

  private initOnDataZoom() {
    this.performanceChart.on('dataZoom', (data) => {
      if (data['from']) {
        this.chartDatePortion = null;
      }
      if (data['startValue']) {
        this.chartStartDate = data['startValue'];
      } else if (data['start']) {
        this.chartStartDate = this.convertPercentToDate(data['start']);
      } else {
        this.chartStartDate = this.minDate.toISOString().split('T')[0];
      }
      if (data['endValue']) {
        this.chartEndDate = data['endValue'];
      } else if (data['end']) {
        this.chartEndDate = this.convertPercentToDate(data['end']);
      } else {
        this.chartEndDate = null;
      }
      this.updateChart();
    });
  }

  private initOnLegendSelect() {
    this.performanceChart.on('legendselectchanged', (data) => {
      this.selectedState = this.readSelectedState(data.selected);
      this.storeSelectedState();
      this.applyMobileLegend();
      this.cd.markForCheck();
    });
    this.performanceChart.on('legendselected', (data) => {
      this.selectedState = this.readSelectedState(data.selected);
      this.storeSelectedState();
      this.applyMobileLegend();
      this.cd.markForCheck();
    });
    this.performanceChart.on('legendunselected', (data) => {
      this.selectedState = this.readSelectedState(data.selected);
      this.storeSelectedState();
      this.applyMobileLegend();
      this.cd.markForCheck();
    });
  }

  /**
   * The full legend selection. A legend event only reports the names listed in legend.data, and the
   * phone legend leaves the mood indexes out while MOOD is off, so there the legend model's own
   * selection (which keeps every series) is read instead. Without it the mood keys would drop out of
   * selectedState, which toggleMood and getMoodCurrentState both iterate. Read off the model rather
   * than getOption(), which deep-clones every series' data and these events fire on each slider move.
   */
  private readSelectedState(eventSelected: Record<string, boolean>): Record<string, boolean> {
    if (!this.chartIsMobile) {
      return eventSelected;
    }
    return {...eventSelected, ...this.performanceChart.getModel?.()?.getComponent('legend')?.option?.selected};
  }

  private convertPercentToDate(value: number): string {
    const daysFromMin = Math.round(this.daysBetween * (value / 100)) + 1;
    const startDate = this.addDays(this.minDate, daysFromMin);
    return startDate.toISOString().split('T')[0];
  }

  private addDays(date, days) {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  }

  private updateChart() {
    this.performanceChart.setOption({series: this.generateSeries()});
    this.applyMobileLegend();
    this.cd.markForCheck();
  }

  private getChartData(enableSelectedState = true) {
    this.loadingChartData = true;
    const params: any = {monthago: this.CHART_DATA_RANGE};
    if (this.portfolioId) {
      params.portfolioIds = this.portfolioId;
    }
    this.performanceDashboardService.getChartData(params).subscribe({
      next: value => {
        // TODO: validate if new data and update instead of build
        this.chartData = value;
        this.initMoodIndexPresets();
        if (enableSelectedState) {
          this.initSelectedState();
        }
        this.initLnDataMap();
        this.loadingChartData = false;
        this.initChart();
      },
      // Clearing the flag here too, not only on complete: an errored observable never completes, so
      // a failed chart call used to leave the spinner turning for as long as the page stayed open.
      error: error1 => {
        this.loadingChartData = false;
        this.bannerNotificationService.error(error1);
        this.cd.markForCheck();
      }
    });
  }

  private initMoodIndexPresets() {
    const dateSet: Set<string> = new Set<string>();
    this.chartData.forEach(chart => {
      if (this.isMoodIndexSeries(chart.name) && chart.financialHistory && chart.financialHistory[0] && chart.financialHistory[0][0]) {
        dateSet.add(chart.financialHistory[0][0]);
      }
    });
    dateSet.forEach(date => {
      const year = Number(date.split('-')[0]);
      const yearEndString = 'YE' + String(year - 1).slice(-2);
      if (year !== DateTime.now().year) {
        this.moodIndexPresetDateMap.set(yearEndString, `${year}-01-01`);
      }
    });
  }

  private initSelectedState() {
    const storedSelectedState = this.localStorageService.get(this.selectedStateStorageKey);
    if (storedSelectedState) {
      this.selectedState = storedSelectedState;
    } else {
      this.chartData.forEach(item => {
        this.selectedState[item.name] =
          item.name === this.RA_CAPITAL_PORTFOLIO ||
          item.name === this.IBB_INDEX ||
          item.name === this.XBI_INDEX ||
          item.name === this.RA_NET_OF_FEES ||
          item.name === this.YE23_MOOD_INDEX ||
          item.name === this.YE24_MOOD_INDEX;
      });
      this.storeSelectedState();
    }
  }

  private storeSelectedState() {
    if (this.enableLocalStorage) {
      this.localStorageService.set(this.selectedStateStorageKey, this.selectedState);
    }
  }

  private initLnDataMap() {
    this.lnDataMap = new Map<string, Map<string, number>>();
    this.chartData.forEach(item => {
      this.lnDataMap.set(item.name, new Map<string, number>());
      item.financialHistory.forEach(fh => {
        this.lnDataMap.get(item.name).set(fh[0], Math.log((fh[1] / 100) + 1));
      });
    });
    this.initMinMaxDate();
  }

  private initMinMaxDate() {
    let minDate = null;
    let maxDate = null;
    let raMinDate = null;
    this.lnDataMap.forEach((value, key) => {
      value.forEach((value1, key1) => {
        if (!minDate) {
          minDate = key1;
        }
        if (!maxDate) {
          maxDate = key1;
        }
        if (key1 > maxDate) {
          maxDate = key1;
        }
        if (key1 < minDate) {
          minDate = key1;
        }
        if (key === this.RA_CAPITAL_PORTFOLIO) {
          if (!raMinDate) {
            raMinDate = key1;
          }
          if (key1 < raMinDate) {
            raMinDate = key1;
          }
        }
      });
    });
    this.minDate = this.getDateFromHistoryDate(minDate);
    this.maxDate = this.getDateFromHistoryDate(maxDate);
    this.raCapitalPortfolioMinDate = raMinDate;
    this.daysBetween = Math.round((this.maxDate.valueOf() - this.minDate.valueOf()) / (1000 * 60 * 60 * 24));
  }

  private initChart() {
    this.chartIsMobile = isMobileViewport();
    // notMerge below brings the legend back, so a rebuild mid-touch must not leave it flagged hidden.
    this.legendHiddenForTouch = false;
    this.performanceChart.setOption({
      color: this.chartService.chartConfig.color,
      legend: {
        data: this.buildLegendData(this.chartData.map(item => item.name)),
        selected: this.selectedState,
        x: 'right',
        right: '15%',
        // Pin the legend to the top explicitly. Leaving it unset used to be enough, but echarts 6
        // changed the legend default from `top: 0` to `bottom: <token>`, which dropped the legend
        // onto the dataZoom slider at the bottom of the chart.
        top: 0,
        itemGap: 12,
        itemWidth: 11,
        itemHeight: 11,
        padding: [7, 60, 0, 0],
        height: 50
      },
      toolbox: {
        show: true,
        height: 50,
        padding: 0,
        feature: {
          dataZoom: {
            yAxisIndex: 'none',
            title: {
              zoom: 'Drag to Zoom',
              back: 'Back'
            }
          }
        }
      },
      tooltip: this.chartIsMobile ? this.buildMobileTooltip() : this.buildDesktopTooltip(),
      grid: {
        left: '3%',
        right: '4%',
        bottom: 50,
        containLabel: true
      },
      dataZoom: [
        {
          type: 'slider',
          show: true,
          startValue: this.chartStartDate,
          endValue: this.chartEndDate ? this.chartEndDate : null,
          end: this.chartEndDate ? null : 100,
          labelFormatter: (value) => (DateTime.fromMillis(value).toFormat(this.chartService.axisDateFormat))
        }
      ],
      xAxis: [
        {
          type: 'time',
          boundaryGap: '0%',
          axisTick: {
            show: true,
            alignWithLabel: true
          },
          axisLine: {
            show: true,
            onZero: true,
            lineStyle: {
              type: 'dashed',
              color: '#06273A'
            }
          },
          splitLine: {
            show: true,
          },
          axisLabel: {
            formatter: (value) => (DateTime.fromMillis(value).toFormat(this.chartService.axisDateFormat)),
            color: RmsColors.GREY_LIGHT_BLUE,
            fontSize: 11,
            fontFamily: 'Gotham-Medium'
          },
          // The hover card and the phone readout both carry the date, so the axis chip would only repeat it.
          axisPointer: {
            label: {
              show: false
            }
          }
        }
      ],
      yAxis: {
        axisLabel: {
          formatter: '{value}%',
          color: RmsColors.GREY_LIGHT_BLUE,
          fontSize: 11,
          fontFamily: 'Gotham-Medium',
          margin: 30,
          align: 'right'
        },
        axisLine: {
          show: true,
          onZero: true,
          lineStyle: {
            type: 'solid',
            color: RmsColors.GREY_BG
          }
        },
        axisTick: {
          show: true,
          alignWithLabel: true,
        },
        axisPointer: {
          label: {
            show: false
          }
        }
      },
      series: this.generateSeries()
    }, {notMerge: true});
    if (this.chartIsMobile) {
      this.performanceChart.setOption(this.buildMobileOptions());
      this.applyMobileLegend();
    } else {
      // Back from a phone layout: drop the inline height so the stylesheet's 615px applies again.
      this.performanceChartElement.nativeElement.style.height = '';
      this.performanceChart.resize();
    }
    this.cd.markForCheck();
  }

  /**
   * Phone overrides merged over initChart's options. The legend moves above the plot at full width,
   * the toolbox goes (drag-to-zoom fights page scroll on touch; the slider still zooms), and the axes
   * and slider shrink to fit a phone.
   */
  private buildMobileOptions(): any {
    const phoneFont = 'Conv_Gotham-Book';
    return {
      legend: {
        left: 0,
        right: 0,
        top: 0,
        padding: 0,
        itemGap: 10,
        itemWidth: 9,
        itemHeight: 9,
        height: 'auto',
        textStyle: {
          fontSize: 11,
          fontFamily: phoneFont,
          color: RmsColors.GREY_LIGHT_BLUE
        }
      },
      toolbox: {
        show: false
      },
      grid: {
        left: 0,
        right: 10,
        bottom: 46,
        containLabel: true
      },
      yAxis: {
        axisLabel: {
          margin: 8,
          fontFamily: phoneFont
        }
      },
      xAxis: [
        {
          splitLine: {
            show: false
          },
          splitNumber: 4,
          axisLabel: {
            formatter: (value) => (DateTime.fromMillis(value).toFormat(this.TOOLTIP_DATE_FORMAT)),
            fontFamily: phoneFont,
            hideOverlap: true
          }
        }
      ],
      dataZoom: [
        {
          showDetail: false,
          height: 22,
          bottom: 6
        }
      ]
    };
  }

  /**
   * On phones, lists the mood indexes in the legend only while MOOD is on, then grows the chart by
   * however much the legend outgrew its two-row default so the plot keeps its height. Called after
   * every change that can turn a series on or off, including autoDisableIndex's.
   */
  private applyMobileLegend() {
    if (!this.chartIsMobile || this.generatingSeries || this.legendHiddenForTouch || !this.chartData) {
      return;
    }
    const showMood = this.getMoodCurrentState();
    const names = this.chartData.map(item => item.name).filter(name => showMood || !this.isCoreBiotechMoodIndex(name));
    this.performanceChart.setOption({legend: {data: this.buildLegendData(names)}});
    this.mobileLegendHeight = this.measureLegendHeight();
    const height = `${this.MOBILE_CHART_HEIGHT + Math.max(0, this.mobileLegendHeight - this.MOBILE_LEGEND_HEIGHT)}px`;
    const element: HTMLElement = this.performanceChartElement.nativeElement;
    if (element.style.height !== height) {
      element.style.height = height;
      this.performanceChart.resize();
    }
    this.performanceChart.setOption({grid: {top: this.mobileLegendHeight + this.MOBILE_LEGEND_PLOT_GAP}});
  }

  /**
   * The legend's rendered height. ECharts has no public API for it, so this reads the private
   * component views and falls back to the two-row height if their shape ever changes.
   */
  private measureLegendHeight(): number {
    try {
      const legendView = this.performanceChart._componentsViews?.find(view => view.__model?.mainType === 'legend');
      const height = legendView?.group?.getBoundingRect()?.height;
      return height ? Math.ceil(height) : this.MOBILE_LEGEND_HEIGHT;
    } catch {
      return this.MOBILE_LEGEND_HEIGHT;
    }
  }

  private buildLegendData(names: Array<string>): Array<{icon: string; name: string}> {
    return names.map(name => ({icon: this.usesLineLegendIcon(name) ? '' : 'circle', name: name}));
  }

  /**
   * Desktop hover: a solid card 16px beside the pointer, flipped to the left near the chart's right
   * edge, and pinned just under the plot's top so it never sits over the legend.
   */
  private buildDesktopTooltip(): any {
    return {
      trigger: 'axis',
      confine: true,
      transitionDuration: 0,
      axisPointer: this.buildTooltipAxisPointer(),
      position: (point: Array<number>, params, dom, rect, size) => {
        const gap = 16;
        const cardWidth = size.contentSize[0];
        const x = point[0] + gap + cardWidth > size.viewSize[0] ? point[0] - gap - cardWidth : point[0] + gap;
        return [Math.max(0, x), this.getPlotTop() + 8];
      },
      backgroundColor: '#FFF',
      borderColor: RmsColors.GREY_BOMBAY,
      borderWidth: 1,
      padding: [10, 12],
      extraCssText: 'border-radius: 6px; box-shadow: 0 4px 14px rgba(16, 38, 56, 0.12);',
      textStyle: {
        fontSize: 12,
        fontFamily: 'Gotham-Medium',
        color: RmsColors.RMS_DARK_PRIMARY_COLOR
      },
      formatter: (params) => this.generateTooltip(params)
    };
  }

  /**
   * Phone readout: just above the plot at full width, in the legend's place while a finger is down,
   * so the values never sit over the lines under the thumb.
   */
  private buildMobileTooltip(): any {
    return {
      trigger: 'axis',
      confine: false,
      transitionDuration: 0,
      axisPointer: this.buildTooltipAxisPointer(),
      // Bottom edge 6px above the plot's top, however many rows the readout has.
      position: (point: Array<number>, params, dom, rect, size) => [
        0,
        Math.max(0, (this.mobileLegendHeight ?? this.MOBILE_LEGEND_HEIGHT) + this.MOBILE_LEGEND_PLOT_GAP - size.contentSize[1] - 6)
      ],
      backgroundColor: '#FFF',
      borderColor: RmsColors.RMS_TEAL_COLOR,
      borderWidth: 1,
      padding: [3, 7],
      extraCssText: 'border-radius: 4px; box-shadow: none;',
      textStyle: {
        fontSize: 10.5,
        fontFamily: 'Gotham-Medium',
        color: RmsColors.RMS_DARK_PRIMARY_COLOR
      },
      formatter: (params) => this.generateMobileReadout(params)
    };
  }

  private buildTooltipAxisPointer(): any {
    return {
      type: 'line',
      snap: true,
      lineStyle: {
        type: 'dashed',
        color: RmsColors.GREY_LIGHT_BLUE
      }
    };
  }

  /** Top of the plot area in chart pixels, read off the grid's laid-out rect; 60 is ECharts' default grid top. */
  private getPlotTop(): number {
    return this.performanceChart.getModel?.()?.getComponent('grid', 0)?.coordinateSystem?.getRect()?.y ?? 60;
  }

  /**
   * The rows both hover layouts list: echarts' params plus RA Net of Fees when its sparse line has no
   * point on the hovered date, sorted high to low.
   */
  private buildTooltipRows(params: Array<any>): Array<any> {
    const rows = [...params];
    // RA Net of Fees is sparse, so echarts omits it from params unless the hovered date lands exactly on
    // one of its points. When it is enabled but absent, pull its nearest current/future point in manually.
    const netOfFeesEntry = this.buildNetOfFeesTooltipEntry(params[0].axisValue, params);
    if (netOfFeesEntry) {
      rows.push(netOfFeesEntry);
    }
    rows.forEach(item => item.sortingKey = item.value[1]);
    return RmsUtilsService.sortByKey(rows, 'sortingKey');
  }

  /** Desktop hover card: the date over a rule, then one row per series with its marker and short label left, value right. */
  private generateTooltip(params: Array<any>): string {
    const date = DateTime.fromMillis(params[0].axisValue).toFormat(this.TOOLTIP_DATE_FORMAT);
    let tt = `<div style="font-size: 12px; line-height: 18px; min-width: 150px">`
      + `<div style="color: ${RmsColors.GREY_LIGHT_BLUE}; margin-bottom: 4px; padding-bottom: 4px;`
      + ` border-bottom: 1px solid ${RmsColors.RMS_GRAY_DETAIL}">${date}</div>`;
    this.buildTooltipRows(params).forEach(item => {
      const conflictDate = item.conflictDate
        ? ` <span style="color: ${RmsColors.GREY_MED}; font-size: 9px; font-style: italic">`
          + `(${DateTime.fromISO(item.conflictDate).toFormat(this.TOOLTIP_DATE_FORMAT)})</span>`
        : '';
      const money = item.value[2] != null
        ? `<span style="color: ${RmsColors.GREY_LIGHT_BLUE}; margin-left: 10px">$${FinancialUtilsService.convertMoney(item.value[2], 2)}</span>`
        : '';
      tt += `<div style="display: flex; align-items: center; justify-content: space-between; gap: 14px; white-space: nowrap">`
        + `<span style="display: inline-flex; align-items: center">${this.buildTooltipMarker(item, 8)}`
        + `<span style="color: ${RmsColors.GREY_LIGHT_BLUE}">${this.getTooltipLabel(item.seriesName)}</span>${conflictDate}</span>`
        + `<span>${this.formatTooltipPercent(item.value[1])}${money}</span></div>`;
    });
    return tt + '</div>';
  }

  /**
   * Phone readout: a date column, centred beside three value columns (two when a mood index is
   * listed, whose labels are longer), filled down then across. It spans the chart and hugs its rows.
   */
  private generateMobileReadout(params: Array<any>): string {
    const rows = this.buildTooltipRows(params);
    const columns = rows.some(item => this.isCoreBiotechMoodIndex(item.seriesName)) ? 2 : 3;
    const rowCount = Math.ceil(rows.length / columns);
    const width = this.performanceChart.getWidth() - 16;
    const date = DateTime.fromMillis(params[0].axisValue).toFormat(this.TOOLTIP_DATE_FORMAT);
    const cells = rows.map(item => `<div style="display: flex; align-items: center; white-space: nowrap">`
      + `${this.buildTooltipMarker(item, 7)}<span style="color: ${RmsColors.GREY_LIGHT_BLUE}">${this.getTooltipLabel(item.seriesName)}</span>`
      + `&nbsp;${this.formatTooltipPercent(item.value[1])}</div>`).join('');
    return `<div style="width: ${width}px; display: grid; align-content: start;`
      + ` grid-template-columns: auto repeat(${columns}, auto); grid-template-rows: repeat(${rowCount}, 14px);`
      + ` grid-auto-flow: column; justify-content: space-between; column-gap: 8px; font-size: 10.5px; line-height: 14px">`
      + `<div style="grid-row: 1 / span ${rowCount}; display: flex; align-items: center; height: 100%; padding-right: 6px;`
      + ` border-right: 1px solid ${RmsColors.RMS_GRAY_DETAIL}; color: ${RmsColors.GREY_LIGHT_BLUE}">${date}</div>`
      + `${cells}</div>`;
  }

  /**
   * A null percent is missing data and shows as '-', but a real 0 has to print as 0.00%: every series
   * is exactly 0 at chartStartDate, so a falsy test blanks the whole leftmost tooltip.
   */
  private formatTooltipPercent(value: number): string {
    const color = value < 0 ? RmsColors.RED_ALERT : RmsColors.RMS_DARK_PRIMARY_COLOR;
    return `<span style="color: ${color}">${value != null ? value.toFixed(2) : '-'}%</span>`;
  }

  /**
   * Short label for a series: a fixed abbreviation, `YE24` / `YE24 −M&A` for the Core Biotech
   * indexes, or the full name for anything else (a generic portfolio). Always HTML-escaped, since
   * portfolio names are user input.
   */
  private getTooltipLabel(seriesName: string): string {
    const fixedLabel = this.TOOLTIP_LABELS.get(seriesName);
    if (fixedLabel) {
      return RmsUtilsService.escapeHtml(fixedLabel);
    }
    const moodMatch = this.CORE_BIOTECH_MOOD_INDEX.exec(seriesName);
    if (!moodMatch) {
      return RmsUtilsService.escapeHtml(seriesName);
    }
    const suffix = moodMatch[2] ? ' \u2212M&A' : '';
    return RmsUtilsService.escapeHtml(moodMatch[1] + suffix);
  }

  /**
   * Solid dot in the series colour, or a ring for the series whose legend icon is a line, so the
   * tooltip and the legend tell RA, RA Long and RA Net apart the same way. The ring test needs the raw
   * series name: usesLineLegendIcon matches '-M&A', which escaping would turn into '-M&amp;A'.
   */
  private buildTooltipMarker(item: any, size: number): string {
    if (!item.color) {
      return '';
    }
    return this.usesRingTooltipMarker(item.seriesName)
      ? this.buildRingMarker(item.color, size)
      : `<span style="display: inline-block; flex: 0 0 auto; margin-right: 5px; border-radius: 50%; width: ${size}px;`
        + ` height: ${size}px; background-color: ${RmsUtilsService.escapeHtml(item.color)}"></span>`;
  }

  /**
   * Builds a synthetic tooltip entry for the RA Net of Fees line when it is enabled but not already present
   * in the hovered params. Uses the closest plotted point on/after the hovered axis date so the displayed
   * percent matches what the line shows there, and tags the point's own date when it differs from the hover.
   */
  private buildNetOfFeesTooltipEntry(axisValue: number, params: Array<any>): any {
    if (!this.selectedState[this.RA_NET_OF_FEES] || params.some(item => item.seriesName === this.RA_NET_OF_FEES)) {
      return null;
    }
    let closest: [string, number, number] = null;
    let closestMillis = Number.POSITIVE_INFINITY;
    this.raNetOfFeesData.forEach(point => {
      const pointMillis = this.getDateFromHistoryDate(point[0]).valueOf();
      if (pointMillis >= axisValue && pointMillis < closestMillis) {
        closest = point;
        closestMillis = pointMillis;
      }
    });
    if (!closest) {
      return null;
    }
    // Only the colour is needed: generateTooltip builds this row's marker itself, the same way it
    // does for the rows echarts supplies, so the two can't drift apart again.
    return {
      seriesName: this.RA_NET_OF_FEES,
      color: this.getLineStyle(this.RA_NET_OF_FEES).color,
      value: closest,
      conflictDate: closestMillis !== axisValue ? closest[0] : null
    };
  }

  private generateSeries(): Array<any> {
    this.moodIndexColorIndex = 0;
    // Reset so a chart pull that no longer includes RA Net of Fees can't leave stale data behind for the tooltip.
    this.raNetOfFeesData = [];
    this.generatingSeries = true;
    const series = this.chartData.map(item => {
      this.autoDisableIndex(item);
      const name = item.key.includes('-portfolio') && item.name !== this.RA_CAPITAL_PORTFOLIO ? this.GENERIC_PORTFOLIO : item.name;
      const lineStyle = this.getLineStyle(name);
      return {
        id: item.key,
        name: item.name,
        type: 'line',
        showSymbol: false,
        dimensions: ['date', 'percent', 'value'],
        color: lineStyle.color,
        lineStyle: lineStyle,
        data: this.transformFinancialHistory(item)
      };
    });
    this.generatingSeries = false;
    return series;
  }

  private autoDisableIndex(chart: PerformanceDashboardChart) {
    const indexStart = chart.financialHistory && chart.financialHistory[0] && chart.financialHistory[0][0] ?
      this.getDateFromHistoryDate(chart.financialHistory[0][0]) : new Date();
    const graphStart = this.getDateFromHistoryDate(this.chartStartDate);
    const daysDiff = Math.round((indexStart.valueOf() - graphStart.valueOf()) / (1000 * 60 * 60 * 24));
    this.enableLocalStorage = false;
    if (daysDiff >= this.MOOD_INDEX_DAY_BUFFER) {
      this.toggleIndex(true, [chart.name]);
    } else {
      if ((daysDiff < this.MOOD_INDEX_DAY_BUFFER && daysDiff >= 0) && !chart.name.includes('-M&A')) {
        this.toggleIndex(false, [chart.name]);
      }
    }
    this.enableLocalStorage = true;
  }

  private getLineStyle(name: string) {
    switch (name) {
      case this.RA_CAPITAL_PORTFOLIO:
        return { color: '#2A6EF6', width: 2 };
      case this.SNP_500:
        return { color: '#0CEF32', width: 1 };
      case this.RUSSEL_2000:
        return { color: '#FB66FF', width: 1 };
      case this.IBB_INDEX:
        return { color: '#0CC9F3', width: 1 };
      case this.XBI_INDEX:
        return { color: '#007C4C', width: 1 };
      case this.RA_NET_OF_FEES:
        return { color: '#2A6EF6', width: 1, type: 'dashed' };
      case this.YE23_MOOD_INDEX:
        return { color: '#9308B5', width: 1 };
      case this.YE23_MA_MOOD_INDEX:
        return { color: '#9308B5', width: 1, type: 'dashed' };
      case this.YE24_MOOD_INDEX:
        return { color: '#BA4A00', width: 1 };
      case this.YE24_MA_MOOD_INDEX:
        return { color: '#BA4A00', width: 1, type: 'dashed' };
      case this.YE20_MOOD_INDEX:
        return { color: '#FF0100', width: 1 };
      case this.YE20_MA_MOOD_INDEX:
        return { color: '#FF0100', width: 1, type: 'dashed' };
      case this.GENERIC_PORTFOLIO:
        return { color: '#2A6EF6', width: 1 };
      default:
        return { color: '#000000', width: 1 };
    }
  }

  private getMoodIndexColor() {
    const color = this.moodIndexColors[this.moodIndexColorIndex];
    this.moodIndexColorIndex += 1;
    if (this.moodIndexColorIndex > (this.moodIndexColors.length - 1)) {
      this.moodIndexColorIndex = 0;
    }
    return color;
  }

  private transformFinancialHistory(chart: PerformanceDashboardChart): Array<[string, number, number]> {
    // Build fresh tuples so date/percent rewrites below never mutate the shared chartData arrays.
    const financialHistory: Array<[string, number, number]> = chart.financialHistory.map(value => [value[0], value[1], value[2]]);
    if (!this.lnDataMap.get(chart.name).has(this.chartStartDate)) {
      financialHistory.unshift([this.chartStartDate, 0, null]);
    }
    // Compute the percent while the original date is intact, since lnDataMap is keyed by the original dates.
    financialHistory.forEach(value => {
      value[1] = this.transformDataPoint(chart.name, value);
    });
    // RA Net of Fees is monthly data whose points can land on weekends. Round those to the previous
    // weekday before filtering so the points survive the shared weekend filter instead of disappearing.
    // Note: this also rounds the chartStartDate anchor point above if chartStartDate itself falls on a
    // weekend, which could theoretically collide with a real data point rounded to the same Friday.
    if (chart.name === this.RA_NET_OF_FEES) {
      financialHistory.forEach(value => {
        value[0] = this.roundWeekendToPreviousWeekday(value[0]);
      });
    }
    const filtered = financialHistory.filter(value => {
      const day = this.getDateFromHistoryDate(value[0]).getDay();
      return value[0] === this.chartStartDate || (day !== 0 && day !== 6);
    });
    if (chart.name === this.RA_NET_OF_FEES) {
      this.raNetOfFeesData = filtered;
    }
    return filtered;
  }

  /**
   * Rounds a weekend date (yyyy-MM-dd) back to the nearest weekday in the past: Saturday and Sunday
   * both collapse to the previous Friday. Weekday dates are returned unchanged.
   */
  private roundWeekendToPreviousWeekday(date: string): string {
    const dateTime = DateTime.fromISO(date);
    if (dateTime.weekday === 6) {
      return dateTime.minus({ days: 1 }).toFormat('yyyy-MM-dd');
    }
    if (dateTime.weekday === 7) {
      return dateTime.minus({ days: 2 }).toFormat('yyyy-MM-dd');
    }
    return date;
  }

  private transformDataPoint(indexName: string, dataPoint: [string, number, number]) {
    if (dataPoint[0] <= this.chartStartDate) {
      return 0;
    } else {
      let sum = 0;
      this.lnDataMap.get(indexName).forEach((value, key, map) => {
        if (key > this.chartStartDate && key <= dataPoint[0]) {
          sum += value;
        }
      });
      return (Math.exp(sum) - 1) * 100;
    }
  }

  /**
   * Whether a series name is a mood index, i.e. anything that is not one of the fixed
   * portfolio or benchmark lines. The Mood toggle button and the year-end date presets both
   * key off this, so any new named series has to be excluded here to stay out of them.
   */
  private isMoodIndexSeries(name: string): boolean {
    return name !== this.RA_CAPITAL_PORTFOLIO && name !== this.RA_LONG_PUBLIC && name !== this.IBB_INDEX &&
      name !== this.XBI_INDEX && name !== this.RA_NET_OF_FEES;
  }

  /**
   * Whether a series is one of the year-end Core Biotech indexes the MOOD button stands for. Matched by
   * name rather than by exclusion like isMoodIndexSeries, which also counts S&P 500, Russell 2000 and a
   * generic portfolio's own line; the phone legend keeps those listed while MOOD is off.
   */
  private isCoreBiotechMoodIndex(name: string): boolean {
    return this.CORE_BIOTECH_MOOD_INDEX.test(name);
  }

  /**
   * Whether the legend should render a line preview instead of a circle. Dashed series need it
   * to show their dash pattern; RA Long Public needs it because a circle swatch would be
   * identical to RA Capital's, leaving the label text as the only way to tell them apart.
   */
  private usesLineLegendIcon(name: string): boolean {
    return name.includes('-M&A') || name === this.RA_NET_OF_FEES;
  }

  /**
   * Whether a series' tooltip marker should be a hollow ring rather than a solid dot. Delegates to
   * usesLineLegendIcon so there is one definition of "this series shows a ring" — the legend and the
   * tooltip drifted apart precisely because the tooltip had no such notion at all.
   */
  private usesRingTooltipMarker(name: string): boolean {
    return this.usesLineLegendIcon(name);
  }

  /**
   * Ring marker matching the legend's line icon. box-sizing keeps the outer footprint the same as the
   * dot's so a ring doesn't shift its row. The colour is escaped because this builds an HTML attribute
   * by concatenation, the same hazard RA-2551 fixed one row over; today's colours are a fixed hex table.
   */
  private buildRingMarker(color: string, size: number): string {
    return `<span style="display:inline-block;flex:0 0 auto;margin-right:5px;border-radius:50%;width:${size}px;height:${size}px;`
      + `box-sizing:border-box;border:2px solid ${RmsUtilsService.escapeHtml(color)};background-color:#fff"></span>`;
  }

  private getDateFromHistoryDate(date: string) {
    const tokens = date.split('-');
    return new Date(Number(tokens[0]), Number(tokens[1]) - 1, Number(tokens[2]));
  }
}
